import { type ApolloClient, gql } from "@apollo/client";
import {
  type AtomData,
  type AtomDescriptor,
  AtomErrors,
  type AtomInfos,
  type CONTEXT_KEY,
  type ContextValues,
  type EntityId,
  type EntityInfos,
  type ExportAtomsOptions,
  type ExportAtomsParameters,
  type ExportAtomsZipParameters,
  type ExportAtomsZipResult,
  type FindParameters,
  type FindResult,
  type GetAtomParameters,
  type ITransaction,
  isAtomPatchesPacket,
  isRecord,
  type SubscribeAtomPacket,
} from "@atoms/atom-types";
import { EventEmitter } from "eventemitter3";
import type { Delta } from "jsondiffpatch";
import { diff, patch } from "jsondiffpatch";
import cloneDeep from "lodash/cloneDeep";
import {
  type SubscriptionCallback,
  type SubscriptionCallbacks,
  type SubscriptionErrorCallback,
  type SubscriptionProps,
  subscriptionDocument,
} from "./AtomStoreProvider";
import type {
  ApiErrorHandlingOptions,
  ApiSuccessHandlingOptions,
} from "./apiRequestOptions";
import {
  isConnectionClosedMessage,
  isConnectionFailure,
  markConnectionFailure,
} from "./apollo/connectionFailure";
import {
  isSessionRejection,
  type SessionRejectionCandidate,
} from "./apollo/sessionRejection";
import { toGraphQLRequestError } from "./graphql/GraphQLRequestError";
import { useUserStore } from "./hooks/useMyUserId";
import { getGlobalNotifier } from "./notifications/notifier";
import { notifyApiRequestError } from "./notifications/notifyUserFacingError";
import { resolveUserFacingSuccess } from "./notifications/userFacingErrors";
import { AtomNotFoundError } from "./use-atom/AtomNotFoundError";
import type { SelectorFunction } from "./use-atom/makeUseAtom";
import { toError } from "./use-atom/shared";

/** The server errors of a failed subscription; none for a transport failure. */
function getGraphQLErrorsOf(failure: unknown): readonly unknown[] {
  if (!isRecord(failure)) return [];
  const { graphQLErrors } = failure;
  return Array.isArray(graphQLErrors) ? graphQLErrors : [];
}

/** A string the backend put into the `extensions` of an error. */
function getErrorExtension(error: unknown, key: string): string | undefined {
  if (!isRecord(error) || !isRecord(error.extensions)) return undefined;
  const value = error.extensions[key];
  return typeof value === "string" ? value : undefined;
}

function throwIfApolloQueryErrors(
  errors: readonly { message: string }[] | undefined,
  operationLabel: string,
): void {
  if (!errors?.length) return;
  // The errors come from the server, so code and Sentry event id stay on the
  // error. The message still lists all of them.
  throw toGraphQLRequestError(
    errors,
    operationLabel,
    errors.map((e) => e.message.trim()).join("\n") ||
      `${operationLabel} failed`,
  );
}

/** The parameters of an entity find: any find parameters but `entries: true`. */
export type EntityFindParameters = FindParameters<
  AtomData,
  AtomData,
  string
> & {
  entries?: false;
};

/**
 * Extended transaction interface for frontend that includes findAtoms / findEntity
 */
export interface IFrontendTransaction extends ITransaction {
  findAtoms<
    T extends AtomData,
    FilterData extends AtomData = T,
    P extends FindParameters<T, FilterData, string> = FindParameters<
      T,
      FilterData,
      string
    >,
  >(
    atomType: string,
    parameters: P,
    overrides?: ContextValues,
  ): Promise<FindResult<T, P> | null>;
  exportAtoms<
    T extends AtomData,
    P extends ExportAtomsParameters<T> = ExportAtomsParameters<T>,
  >(
    atomType: string,
    parameters?: P,
    overrides?: ContextValues,
    options?: ExportAtomsOptions,
  ): Promise<string>;

  exportAtomsZip(
    parameters: ExportAtomsZipParameters,
    overrides?: ContextValues,
  ): Promise<ExportAtomsZipResult>;

  findEntity<
    T extends AtomData,
    P extends EntityFindParameters = EntityFindParameters,
  >(
    entityType: string,
    parameters: P,
    overrides?: ContextValues,
  ): Promise<FindResult<T, P> | null>;
}

export class HashValidationError extends Error {
  constructor(
    public hashFromBackend: string,
    public localHash: string,
    public atomType: string,
    public context: ContextValues,
  ) {
    super(
      `Hash validation failed for atom ${atomType} with context ${JSON.stringify(
        context,
      )}. Hash from backend: ${hashFromBackend}, local hash: ${localHash}`,
    );
  }
}
// async function sha256(message: string): Promise<string> {
//   // encode as UTF-8
//   const msgBuffer = new TextEncoder().encode(message);

//   // hash the message
//   const hashBuffer = await crypto.subtle.digest("SHA-256", msgBuffer);

//   // convert ArrayBuffer to Array
//   const hashArray = Array.from(new Uint8Array(hashBuffer));

//   // convert bytes to hex string
//   const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
//   return hashHex;
// }
/** What a subscription that could not start resolves to. */
const CLOSED_SUBSCRIPTION: ZenObservable.Subscription = {
  closed: true,
  unsubscribe: () => undefined,
};

export class AtomStore<AtomType extends string = string> extends EventEmitter {
  private subscriptions: SubscriptionCallbacks = {};

  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  private state: { [key in AtomType]?: any } = {};

  private retries: {
    [path: string]: { count: number; timeout?: ReturnType<typeof setTimeout> };
  } = {};

  /**
   * Per started subscription: set when `refreshAtom` replaced it, so a packet
   * it still delivers is dropped instead of being applied to the new value.
   */
  private readonly subscriptionMarkers = new WeakMap<
    Promise<ZenObservable.Subscription>,
    { superseded: boolean }
  >();

  // Track all subscribe events
  private subscriptionEvents: Array<{
    atomType: AtomType;
    context: ContextValues;
    parameters?: GetAtomParameters;
    timestamp: number;
  }> = [];

  constructor(
    private apolloClient: ApolloClient<object>,
    public readonly atomInfosExtended: AtomInfos<string, string>,
    public readonly entityInfos: EntityInfos<string, CONTEXT_KEY<string>>,
    public onSuccess: (options?: ApiSuccessHandlingOptions) => void = (
      options,
    ) => {
      if (options?.showSuccessToast !== true) {
        return;
      }

      const formatted = resolveUserFacingSuccess({
        successMessage: options.successMessage,
        keyPrefix: options?.keyPrefix ?? "api",
        operationContext: options?.operationContext,
      });
      getGlobalNotifier().notify(formatted.level, formatted.message, {
        key: formatted.key ?? `api:${formatted.message}`,
      });
    },
    public onError: (
      error: unknown,
      options?: ApiErrorHandlingOptions,
    ) => void = (error, options) => {
      notifyApiRequestError(error, options);
    },
  ) {
    super();
  }

  public getTransaction(context: ContextValues = {}): IFrontendTransaction {
    const aquireAtomValue = async <T extends AtomData>(
      atomType: AtomType,
      overrides?: ContextValues,
      parameters?: GetAtomParameters,
    ): Promise<T> => {
      const combinedContext = {
        ...context,
        ...overrides,
      };
      // Throws the stored error of a failed subscription.
      const existingValue = this.getAtom(atomType, combinedContext, parameters);
      if (existingValue) {
        return Promise.resolve(existingValue);
      }
      if (existingValue === null) {
        throw new AtomNotFoundError(`Atom ${atomType} not found`, atomType);
      }
      return new Promise((resolve, reject) => {
        const callback = async (value: T | null) => {
          await this.unsubscribe(
            atomType,
            combinedContext,
            callback,
            parameters,
          );
          if (value) {
            resolve(value);
          } else {
            reject(
              new AtomNotFoundError(`Atom ${atomType} not found`, atomType),
            );
          }
        };
        const onError = async (error: Error) => {
          await this.unsubscribe(
            atomType,
            combinedContext,
            callback,
            parameters,
          );
          reject(error);
        };
        try {
          this.subscribe(
            atomType,
            combinedContext,
            callback,
            undefined,
            parameters,
            onError,
          );
        } catch (error) {
          reject(error);
        }
      });
    };

    const findAtoms = async <
      T extends AtomData,
      FilterData extends AtomData = T,
      P extends FindParameters<T, FilterData, string> = FindParameters<
        T,
        FilterData,
        string
      >,
    >(
      atomType: string,
      parameters: P,
      overrides?: ContextValues,
    ): Promise<FindResult<T, P> | null> => {
      return this.executeFindAtoms<T, FilterData, P>(
        atomType,
        parameters,
        context,
        overrides,
      );
    };

    const exportAtoms = async <
      T extends AtomData,
      P extends ExportAtomsParameters<T> = ExportAtomsParameters<T>,
    >(
      atomType: string,
      parameters?: P,
      overrides?: ContextValues,
      options?: ExportAtomsOptions,
    ): Promise<string> => {
      return this.executeExportAtoms<T, P>(
        atomType,
        parameters,
        context,
        overrides,
        options,
      );
    };

    const exportAtomsZip = async (
      parameters: ExportAtomsZipParameters,
      overrides?: ContextValues,
    ): Promise<ExportAtomsZipResult> => {
      return this.executeExportAtomsZip(parameters, context, overrides);
    };

    const findEntity = async <
      T extends AtomData,
      P extends EntityFindParameters = EntityFindParameters,
    >(
      entityType: string,
      parameters: P,
      overrides?: ContextValues,
    ): Promise<FindResult<T, P> | null> => {
      return this.executeFindEntity<T, P>(
        entityType,
        parameters,
        context,
        overrides,
      );
    };

    return {
      getEntry: <K extends CONTEXT_KEY<string>>(
        key: K,
      ): EntityId<K> | undefined => context[key] as EntityId<K> | undefined,
      getActingUserOrThrow: () => {
        const userId = useUserStore.getState().userId;
        if (!userId) {
          throw new Error("No user ID found");
        }
        return userId;
      },
      aquireAtomValue,
      findAtoms,
      exportAtoms,
      exportAtomsZip,
      findEntity,
    };
  }

  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  public updateErrorCallback(callback: (error: any) => void): void {
    this.onError = callback;
  }

  public getApolloClient(): ApolloClient<object> {
    return this.apolloClient;
  }

  /**
   * Execute a findAtoms query against the GraphQL backend
   * @param atomType The atom type to query
   * @param parameters Find parameters (filters, pagination, etc.)
   * @param context Context values for the query
   * @param overrides Optional context overrides
   * @returns Find result or null if no atoms found
   */
  public async executeFindAtoms<
    T extends AtomData,
    FilterData extends AtomData = T,
    P extends FindParameters<T, FilterData, string> = FindParameters<
      T,
      FilterData,
      string
    >,
  >(
    atomType: string,
    parameters: P,
    context: ContextValues,
    overrides?: ContextValues,
  ): Promise<FindResult<T, P> | null> {
    const combinedContext = {
      ...context,
      ...overrides,
    };

    try {
      const variables = {
        context: combinedContext,
        atomType,
        parameters: parameters,
        overrides: overrides || {},
      };

      const result = await this.apolloClient.query<{
        findAtoms: FindResult<T, P>;
      }>({
        query: gql`
          query findAtoms(
            $context: JSON!
            $atomType: String!
            $parameters: JSON!
            $overrides: JSON
          ) {
            findAtoms(
              context: $context
              atomType: $atomType
              parameters: $parameters
              overrides: $overrides
            )
          }
        `,
        fetchPolicy: "no-cache",
        errorPolicy: "all",
        variables,
      });

      throwIfApolloQueryErrors(result.errors, "findAtoms");
      return result.data?.findAtoms ?? null;
    } catch (error) {
      console.error("Error in executeFindAtoms:", error);
      throw error;
    }
  }

  /**
   * Execute an exportAtoms query against the GraphQL backend
   * @param atomType The atom type to export
   * @param parameters Optional find parameters (without includeAtomData)
   * @param context Context values for the query
   * @param overrides Optional context overrides
   * @param options Optional export options
   * @returns Export content
   */
  public async executeExportAtoms<
    T extends AtomData,
    P extends ExportAtomsParameters<T> = ExportAtomsParameters<T>,
  >(
    atomType: string,
    parameters: P | undefined,
    context: ContextValues,
    overrides?: ContextValues,
    options?: ExportAtomsOptions,
  ): Promise<string> {
    const combinedContext = {
      ...context,
      ...overrides,
    };

    try {
      const variables = {
        context: combinedContext,
        atomType,
        parameters: parameters ?? {},
        overrides: overrides ?? {},
        options: options ?? {},
      };

      const result = await this.apolloClient.query<{
        exportAtoms: string;
      }>({
        query: gql`
          query exportAtoms(
            $context: JSON!
            $atomType: String!
            $parameters: JSON
            $overrides: JSON
            $options: JSON
          ) {
            exportAtoms(
              context: $context
              atomType: $atomType
              parameters: $parameters
              overrides: $overrides
              options: $options
            )
          }
        `,
        fetchPolicy: "no-cache",
        errorPolicy: "all",
        variables,
      });

      throwIfApolloQueryErrors(result.errors, "exportAtoms");
      return result.data?.exportAtoms ?? "";
    } catch (error) {
      console.error("Error in executeExportAtoms:", error);
      throw error;
    }
  }

  /**
   * Execute an exportAtomsZip query against the GraphQL backend
   * @param parameters Atom types and optional export options
   * @param context Context values for the query
   * @param overrides Optional context overrides
   * @returns Base64 encoded ZIP export payload
   */
  public async executeExportAtomsZip(
    parameters: ExportAtomsZipParameters,
    context: ContextValues,
    overrides?: ContextValues,
  ): Promise<ExportAtomsZipResult> {
    const combinedContext = {
      ...context,
      ...overrides,
    };

    try {
      const variables = {
        context: combinedContext,
        atomTypes: parameters.atomTypes,
        fileName: parameters.fileName,
        overrides: overrides ?? {},
        options: parameters.options ?? {},
      };

      const result = await this.apolloClient.query<{
        exportAtomsZip: ExportAtomsZipResult;
      }>({
        query: gql`
          query exportAtomsZip(
            $context: JSON!
            $atomTypes: [String!]!
            $fileName: String!
            $overrides: JSON
            $options: JSON
          ) {
            exportAtomsZip(
              context: $context
              atomTypes: $atomTypes
              fileName: $fileName
              overrides: $overrides
              options: $options
            )
          }
        `,
        fetchPolicy: "no-cache",
        errorPolicy: "all",
        variables,
      });

      throwIfApolloQueryErrors(result.errors, "exportAtomsZip");
      return (
        result.data?.exportAtomsZip ?? {
          fileName: "project-atoms.zip",
          contentBase64: "",
          exportedAtomTypes: [],
        }
      );
    } catch (error) {
      console.error("Error in executeExportAtomsZip:", error);
      throw error;
    }
  }

  /**
   * Execute {@link TransactionManager.findEntity} via GraphQL (non-subscription).
   * An entity find reads rows, never entries: `entries: true` does not compile.
   */
  public async executeFindEntity<
    T extends AtomData,
    P extends EntityFindParameters = EntityFindParameters,
  >(
    entityType: string,
    parameters: P,
    context: ContextValues,
    overrides?: ContextValues,
  ): Promise<FindResult<T, P> | null> {
    try {
      const variables = {
        context,
        entityType,
        parameters,
        overrides: overrides ?? {},
      };

      const result = await this.apolloClient.query<{
        findEntity: FindResult<T, P>;
      }>({
        query: gql`
          query findEntity(
            $context: JSON!
            $entityType: String!
            $parameters: JSON!
            $overrides: JSON
          ) {
            findEntity(
              context: $context
              entityType: $entityType
              parameters: $parameters
              overrides: $overrides
            )
          }
        `,
        fetchPolicy: "no-cache",
        errorPolicy: "all",
        variables,
      });

      throwIfApolloQueryErrors(result.errors, "findEntity");
      return result.data?.findEntity ?? null;
    } catch (error) {
      console.error("Error in executeFindEntity:", error);
      throw error;
    }
  }

  public getPresentAtoms(): AtomDescriptor[] {
    return Object.keys(this.subscriptions) as AtomDescriptor[];
  }

  private formatParameters(parameters?: GetAtomParameters): string {
    if (!parameters || Object.keys(parameters).length === 0) {
      return "";
    }
    const paramString = Object.entries(parameters)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}:${JSON.stringify(value)}`)
      .join(",");
    return `?${paramString}`;
  }

  private notifyAllCallbacks<RType extends object>(
    atomId: string,
    callbacks: Array<SubscriptionCallback<RType>>,
    value: RType | null,
    patches: Delta | undefined,
    initial?: boolean,
  ): void {
    for (const cb of [...callbacks]) {
      try {
        cb(value, patches, initial);
      } catch (e) {
        console.error(`Error in callback for ${atomId}:`, e);
      }
    }
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
  }

  private isDoesNotExistPacket(value: unknown): value is {
    __doesNotExist: unknown;
  } {
    return this.isRecord(value) && "__doesNotExist" in value;
  }

  private isDeltaPacket(value: unknown): value is { __delta: Delta } {
    if (!this.isRecord(value)) return false;
    if (!("__delta" in value)) return false;
    const maybeDelta = (value as { __delta: unknown }).__delta;
    return typeof maybeDelta !== "undefined";
  }

  private isFullValuePacket<RType extends object>(
    value: unknown,
  ): value is { __value: RType } {
    return this.isRecord(value) && "__value" in value;
  }

  /** Answers the `refreshAtom` callers of an entry. */
  private settleRefresh(sub: SubscriptionProps, received: boolean): void {
    const waiters = sub.refreshWaiters;
    if (!waiters || waiters.length === 0) return;
    sub.refreshWaiters = [];
    for (const resolve of waiters) {
      resolve(received);
    }
  }

  private handleDoesNotExist<RType extends object>(atomId: string): void {
    const sub = this.subscriptions[atomId];
    if (!sub) return;
    sub.lastValue = null;
    sub.error = undefined;
    this.notifyAllCallbacks<RType>(
      atomId,
      sub.callbacks as Array<SubscriptionCallback<RType>>,
      null,
      {},
    );
    this.settleRefresh(sub, true);
  }

  private handleDeltaPacket<RType extends object>(
    atomId: string,
    type: AtomType,
    __delta: Delta,
  ): void {
    const sub = this.subscriptions[atomId];
    if (!sub) return;
    if (!__delta || Object.keys(__delta).length === 0) return;
    const newValueRaw = cloneDeep(sub.lastValue ?? {}) as RType;
    patch(newValueRaw, __delta);
    sub.lastValue = newValueRaw as object;
    sub.error = undefined;
    // biome-ignore lint/suspicious/noExplicitAny: legacy state shape
    (this.state as any)[type] = newValueRaw as unknown as object;
    this.notifyAllCallbacks<RType>(
      atomId,
      sub.callbacks as Array<SubscriptionCallback<RType>>,
      newValueRaw,
      __delta,
    );
  }

  private handleFullValue<RType extends object>(
    atomId: string,
    type: AtomType,
    value: RType,
  ): void {
    const sub = this.subscriptions[atomId];
    if (!sub) return;
    sub.lastValue = value as object;
    sub.error = undefined;
    // biome-ignore lint/suspicious/noExplicitAny: legacy state shape
    (this.state as any)[type] = value as unknown as object;
    const patchesForCallback = diff({}, value);
    this.notifyAllCallbacks<RType>(
      atomId,
      sub.callbacks as Array<SubscriptionCallback<RType>>,
      value,
      (patchesForCallback || undefined) as Delta | undefined,
      true,
    );
    this.settleRefresh(sub, true);
  }

  private handleComplete(atomId: string): void {
    const sub = this.subscriptions[atomId];
    if (!sub) return;
    sub.completed = true;
    this.settleRefresh(sub, false);
    // Do not coerce lastValue to null here:
    // - `null` is reserved for the stable "does not exist" state [[memory:12469949]]
    // - subscription completion can happen during transient disconnects/reconnects.
    // We keep the last known value so the UI can continue rendering stale data,
    // while the connection layer handles resubscribe/refetch on reconnect.
  }

  /**
   * A subscription ended with an error. What the entry becomes:
   *
   * - a rejected session is never an error of the page: the login handles
   *   it, the entry keeps its state and is reopened on the next connect;
   * - "atom does not exist" (by its code) is the not-found state;
   * - a transport failure (no server error) is an error only while nothing
   *   was received yet, and then the failure itself, never wrapped;
   * - a server error is the error of the entry, except that an entry which
   *   already has a value loses it only for a missing permission. With any
   *   other error it keeps the value and is reopened on the next connect.
   */
  private handleSubscriptionFailure(atomId: string, failure: unknown): void {
    const sub = this.subscriptions[atomId];
    if (!sub) return;
    this.applySubscriptionFailure(atomId, sub, failure);
    // A not-found answer has settled them with `true` already.
    this.settleRefresh(sub, false);
  }

  private applySubscriptionFailure(
    atomId: string,
    sub: SubscriptionProps,
    failure: unknown,
  ): void {
    const graphQLErrors = getGraphQLErrorsOf(failure);
    const primary = graphQLErrors[0];
    const hasValue = sub.lastValue !== undefined;
    const errorClass = getErrorExtension(primary, "errorClass");

    if (primary === undefined) {
      if (hasValue) {
        sub.completed = true;
        return;
      }
      // An `Error` stays the same instance. When the transport says that
      // the connection is gone, an error screen shows the connection notice
      // for it, not its message. Anything else that arrives without a server
      // error (an exception in the link chain) stays a defect of the client.
      const error = toError(failure);
      if (
        isConnectionFailure(error) ||
        isConnectionClosedMessage(error.message)
      ) {
        markConnectionFailure(error);
      }
      this.failSubscription(atomId, sub, error);
      return;
    }
    if (
      isRecord(primary) &&
      isSessionRejection(primary as SessionRejectionCandidate)
    ) {
      sub.completed = true;
      return;
    }
    if (
      getErrorExtension(primary, "errorCode") === AtomErrors.CannotProvideAtom
    ) {
      sub.completed = true;
      this.handleDoesNotExist(atomId);
      return;
    }
    if (hasValue && errorClass !== "permission") {
      sub.completed = true;
      return;
    }
    // Only an error the backend classified becomes a `GraphQLRequestError`:
    // the backend reported it. Any other error stays visible to the
    // client's own error reporting.
    this.failSubscription(
      atomId,
      sub,
      errorClass
        ? toGraphQLRequestError(graphQLErrors, "subscribeAtom")
        : toError(primary),
    );
  }

  private failSubscription(
    atomId: string,
    sub: SubscriptionProps,
    error: Error,
  ): void {
    sub.error = error;
    sub.lastValue = undefined;
    // A callback may unsubscribe itself: a `Map` can lose entries while it
    // is iterated.
    for (const onError of sub.errorCallbacks?.values() ?? []) {
      try {
        onError(error);
      } catch (error_) {
        console.error(`Error in error callback for ${atomId}:`, error_);
      }
    }
    // Nobody listens (the last listener left within the grace period): the
    // next subscriber must ask the server again, not get this error.
    if (this.listenerCount(atomId) === 0) {
      this.dropSubscription(atomId, sub);
    }
  }

  public pathForAtomWithContext(
    type: AtomType,
    context: ContextValues,
    parameters?: GetAtomParameters,
  ): string {
    const entityType = this.atomInfosExtended[type]?.entityType;
    if (!entityType) {
      throw new Error(`No entity type mapping for atom ${type}`);
    }

    const entityInfo = this.entityInfos[entityType];
    if (!entityInfo) {
      throw new Error(`No entity info for entity type ${entityType}`);
    }

    for (const path of entityInfo.validContexts) {
      const contextRecord = context as Record<string, string | undefined>;
      const x = path.map((p) => {
        if (p === null) {
          return undefined;
        }
        return contextRecord[p];
      });
      if (x.every((v) => v !== undefined)) {
        let atomPath = `${path
          .map((p) => {
            if (p === null) {
              return "";
            }
            return `/${p.replace(/<|>/g, "")}:${contextRecord[p]}`;
          })
          .join("")}/${type}`;

        // Include parameters in the path if they exist
        const paramsSuffix = this.formatParameters(parameters);
        if (paramsSuffix) atomPath += paramsSuffix;

        return atomPath;
      }
    }
    console.debug(
      "Atom:",
      type,
      "Given context:",
      Object.entries(context)
        .filter(([_k, v]) => v !== undefined)
        .map(([k, v]) => `${k}: ${v}`)
        .join(", "),
      "Required:",
      entityInfo.validContexts.map((p) => p.join(", ")).join(" OR "),
    );
    throw new Error(`No valid context for atom ${type}`);
  }

  // biome-ignore lint/suspicious/noExplicitAny: legacy code
  public getAtom<RType = any>(
    type: AtomType,
    context: ContextValues,
    parameters?: GetAtomParameters,
  ): RType | null | undefined {
    const atomId = this.pathForAtomWithContext(type, context, parameters);

    if (this.subscriptions[atomId]?.error) {
      throw this.subscriptions[atomId]?.error;
    }

    const lastValue = this.subscriptions[atomId]?.lastValue;
    // Important: `null` means "atom does not exist" (intentional, stable state),
    // while `undefined` means "not yet received".
    if (typeof lastValue === "undefined") return undefined;
    return lastValue as RType | null;
  }

  public subscribe<RType extends object = object>(
    type: AtomType,
    context: ContextValues,
    callback: SubscriptionCallback<RType>,
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    selector: SelectorFunction<RType, any> = (value): RType => value,
    parameters?: GetAtomParameters,
    onError?: SubscriptionErrorCallback,
  ): void {
    const atomId = this.pathForAtomWithContext(type, context, parameters);
    const matchingSubscription = this.subscriptions[atomId];
    if (!matchingSubscription) {
      if (this.retries[atomId]) {
        clearTimeout(this.retries[atomId]?.timeout);
        delete this.retries[atomId];
      }

      this.retries[atomId] = { count: 0 };

      const getSubscription = (): Promise<ZenObservable.Subscription> =>
        this.startSubscription<RType>(atomId, type, context, parameters);
      this.subscriptions[atomId] = {
        selectors: [selector],
        callbacks: [callback as SubscriptionCallback<object>],
        errorCallbacks: new Map(),
        lastValue: undefined,
        current: getSubscription(),
        atomType: type,
        context: context as Record<string, unknown>,
        parameters: parameters as Record<string, unknown> | undefined,
      };
      // Track the subscribe event
      this.subscriptionEvents.push({
        atomType: type,
        context,
        parameters,
        timestamp: Date.now(),
      });
    } else {
      if (this.subscriptions[atomId]?.lastValue !== undefined) {
        const rawLastValue = this.subscriptions[atomId]
          ?.lastValue as RType | null;
        const valueForCallback = rawLastValue;
        const patchesForCallback = diff({}, valueForCallback);
        // biome-ignore lint/suspicious/noExplicitAny: legacy code
        callback(valueForCallback, (patchesForCallback || []) as any, true);
      }
      this.subscriptions[atomId]?.callbacks.push(
        callback as SubscriptionCallback<object>,
      );
      this.subscriptions[atomId]?.selectors.push(selector);
    }
    if (onError) {
      this.subscriptions[atomId]?.errorCallbacks?.set(callback, onError);
    }
    this.addListener(atomId, callback);
  }

  private startSubscription<RType extends object = object>(
    atomId: string,
    type: AtomType,
    context: ContextValues,
    parameters?: GetAtomParameters,
  ): Promise<ZenObservable.Subscription> {
    const marker = { superseded: false };
    const started = new Promise<ZenObservable.Subscription>((resolve) => {
      try {
        resolve(
          this.openSubscription<RType>(
            atomId,
            type,
            context,
            parameters,
            marker,
          ),
        );
      } catch (error) {
        // A start that throws reaches no observer callback: fail the entry
        // as the transport would, once the caller has stored it.
        resolve(CLOSED_SUBSCRIPTION);
        queueMicrotask(() => {
          if (!marker.superseded) {
            this.handleSubscriptionFailure(atomId, error);
          }
        });
      }
    });
    this.subscriptionMarkers.set(started, marker);
    return started;
  }

  private openSubscription<RType extends object = object>(
    atomId: string,
    type: AtomType,
    context: ContextValues,
    parameters: GetAtomParameters | undefined,
    marker: { superseded: boolean },
  ): ZenObservable.Subscription {
    return this.apolloClient
      .subscribe<{
        subscribeAtom: SubscribeAtomPacket<RType, AtomType>;
      }>({
        query: subscriptionDocument,
        fetchPolicy: "no-cache",
        variables: { type, context, parameters },
      })
      .subscribe({
        next: ({ data }) => {
          if (!data || marker.superseded) return;
          const maybe = data.subscribeAtom as unknown;
          if (this.isDoesNotExistPacket(maybe)) {
            this.handleDoesNotExist<RType>(atomId);
          } else if (
            isAtomPatchesPacket(maybe as SubscribeAtomPacket<object, AtomType>)
          ) {
            // this has been obsoleted by the delta packet, but there still exists a unfinished partial update functionality in the atom.resolver
            throw new Error("partial updates not implemented");
          } else if (this.isDeltaPacket(maybe)) {
            const { __delta } = maybe;
            this.handleDeltaPacket<RType>(atomId, type, __delta);
          } else if (this.isFullValuePacket<RType>(maybe)) {
            this.handleFullValue<RType>(atomId, type, maybe.__value);
          } else {
            const newValueRaw = data.subscribeAtom as RType; // backward compatibility
            this.handleFullValue<RType>(atomId, type, newValueRaw);
          }
        },
        complete: () => {
          if (marker.superseded) return;
          this.handleComplete(atomId);
        },
        error: (failure: unknown) => {
          if (marker.superseded) return;
          this.handleSubscriptionFailure(atomId, failure);
        },
      });
  }

  /**
   * Restarts the subscription of an atom: the old one is closed, and the new
   * one starts with the full value, followed by deltas in order. For a
   * caller that knows the store missed a change (the server rejected a save
   * as stale). A full value fetched next to the subscription could be
   * overtaken by a delta computed against an older state; a restart cannot.
   *
   * Resolves `true` once the new subscription delivered a value (or "does
   * not exist"), `false` when it failed or ended without one, and `false` at
   * once when nothing subscribes to the atom. The store keeps the last value
   * until the new one arrives. A restarted stream that delivers nothing and
   * reports no error (a dead socket) leaves the promise pending until the
   * keepalive, a reconnect or `resubscribeAll` answers it; a save button
   * waiting for it stays loading meanwhile.
   */
  public async refreshAtom(
    type: AtomType,
    context: ContextValues,
    parameters?: GetAtomParameters,
  ): Promise<boolean> {
    const atomId = this.pathForAtomWithContext(type, context, parameters);
    const sub = this.subscriptions[atomId];
    if (!sub) return false;
    const previous = sub.current;
    const previousMarker = this.subscriptionMarkers.get(previous);
    if (previousMarker) {
      previousMarker.superseded = true;
    }
    const received = new Promise<boolean>((resolve) => {
      sub.refreshWaiters = [...(sub.refreshWaiters ?? []), resolve];
    });
    let previousSubscription: ZenObservable.Subscription | undefined;
    try {
      previousSubscription = await previous;
    } catch {
      previousSubscription = undefined;
    }
    if (this.subscriptions[atomId] !== sub) {
      this.settleRefresh(sub, false);
      return received;
    }
    // A refresh that started meanwhile restarted it already: its value
    // answers this call too.
    if (sub.current !== previous) {
      return received;
    }
    if (previousSubscription && !previousSubscription.closed) {
      previousSubscription.unsubscribe();
    }
    sub.completed = false;
    sub.error = undefined;
    sub.current = this.startSubscription(atomId, type, context, parameters);
    return received;
  }

  /**
   * Reopen all active atom subscriptions.
   * Intended to be called after a WebSocket reconnect to avoid lost updates.
   */
  public async resubscribeAll(): Promise<void> {
    const entries = Object.entries(this.subscriptions);
    await Promise.all(
      entries.map(async ([atomId, sub]) => {
        // Only resubscribe if something still listens.
        if (this.listenerCount(atomId) === 0) return;

        if (sub.unsubscribeTimeout) {
          clearTimeout(sub.unsubscribeTimeout);
          sub.unsubscribeTimeout = undefined;
        }

        const previous = sub.current;
        let current: ZenObservable.Subscription | undefined;
        try {
          current = await previous;
        } catch {
          current = undefined;
        }
        // Dropped, or restarted by `refreshAtom` meanwhile.
        if (this.subscriptions[atomId] !== sub || sub.current !== previous) {
          return;
        }

        const isClosed = !current || current.closed;
        const shouldReopen =
          isClosed || Boolean(sub.completed) || Boolean(sub.error);
        if (!shouldReopen) return;

        if (!sub.atomType || !sub.context) return;

        // Reset transient flags; preserve lastValue (stale is fine until refreshed).
        sub.completed = false;
        sub.error = undefined;
        const previousMarker = this.subscriptionMarkers.get(previous);
        if (previousMarker) {
          previousMarker.superseded = true;
        }
        sub.current = this.startSubscription(
          atomId,
          sub.atomType as AtomType,
          sub.context as ContextValues,
          sub.parameters as GetAtomParameters | undefined,
        );
      }),
    );
  }

  public getEntityType(type: AtomType): string | undefined {
    return this.atomInfosExtended[type]?.entityType;
  }

  public getDefaultValues(type: AtomType): Record<string, unknown> | undefined {
    return this.atomInfosExtended[type]?.defaultValues;
  }

  public hasPermission(_permission: string, _context: ContextValues): boolean {
    return true;
  }

  public async unsubscribe(
    type: AtomType,
    context: ContextValues,
    // biome-ignore lint/suspicious/noExplicitAny: legacy code
    callback: SubscriptionCallback<any>,
    parameters?: GetAtomParameters,
  ): Promise<void> {
    const atomId = this.pathForAtomWithContext(type, context, parameters);
    this.removeListener(atomId, callback);

    const entry = this.subscriptions[atomId];
    if (!entry) {
      console.debug(`No subscription for ${atomId}`);
      return;
    }

    const index = entry.callbacks.indexOf(callback);
    if (index === -1) {
      console.debug(`Callback not found for ${atomId}`);
      return;
    }
    entry.callbacks.splice(index, 1);
    entry.selectors.splice(index, 1);
    entry.errorCallbacks?.delete(callback);

    await entry.current;
    const isUnused = (): boolean =>
      this.subscriptions[atomId] === entry && this.listenerCount(atomId) === 0;
    if (!isUnused()) return;

    // A failed entry goes at once, so that the next subscriber asks the
    // server again instead of getting the old error. This runs after the
    // effects of a commit: a hook that only resubscribes keeps the entry.
    if (entry.error) {
      this.dropSubscription(atomId, entry);
      return;
    }
    entry.unsubscribeTimeout = setTimeout(() => {
      if (isUnused()) {
        this.dropSubscription(atomId, entry);
      }
    }, 1000);
  }

  private dropSubscription(atomId: string, entry: SubscriptionProps): void {
    if (entry.unsubscribeTimeout) {
      clearTimeout(entry.unsubscribeTimeout);
    }
    this.settleRefresh(entry, false);
    delete this.subscriptions[atomId];
    if (this.retries[atomId]?.timeout) {
      clearTimeout(this.retries[atomId]?.timeout);
    }
    delete this.retries[atomId];
    // The subscription the entry has now: a reconnect may have reopened it
    // since the last listener left.
    void entry.current.then((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  public getSubscriptionEvents() {
    return this.subscriptionEvents;
  }
}
